import React, {useEffect, useRef, useState} from 'react';
import {onAuthStateChanged} from 'firebase/auth';
import {Alert, Box, Button, Stack, Typography} from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import Theme from '../style/Theme';
import {clearReportedMaintenance, getReportedMaintenance, hasOperatorHandoff, subscribeMaintenance} from '../data/maintenance';

export const MAINTENANCE_POLL_MS = 30000;
const POLL_JITTER_MS = 15000;

export const DEFAULT_MAINTENANCE_MESSAGE = 'Ember Tracking is down for scheduled maintenance. Please check back soon.';

// Rendered outside each app's own <Theme>, so it brings the shared one.
export function MaintenanceScreen({message, onCheck, checking = false}) {
    return (
        <Theme>
            <Box sx={{minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3}}>
                <Stack role="status" spacing={2} sx={{alignItems: 'center', maxWidth: 440, textAlign: 'center'}}>
                    <ConstructionIcon color="primary" sx={{fontSize: 56}} />
                    <Typography variant="h5" component="h1">Down for maintenance</Typography>
                    <Typography variant="body1">{message || DEFAULT_MAINTENANCE_MESSAGE}</Typography>
                    <Typography variant="body2" sx={{color: 'text.secondary'}}>
                        This page will refresh on its own when we’re back.
                    </Typography>
                    {onCheck && (
                        <Button variant="outlined" onClick={onCheck} disabled={checking}>
                            {checking ? 'Checking…' : 'Check now'}
                        </Button>
                    )}
                </Stack>
            </Box>
        </Theme>
    );
}

// Outermost wrapper for both SPAs (#302). While the api reports maintenance for
// this project, everything below — the session gate, cache hydration and every
// Firestore listener — is unmounted and a maintenance page shows instead. It
// polls the public status route and reloads when maintenance ends, so an open
// tab comes back on whatever bundle is deployed by then.
//
// Operator sessions are exempt, matching the api: a signed-in token carrying
// `operator: true` sees the app with a banner, and the operator login handoff
// (#operatorToken=…) is let through so that session can be started at all.
export default function MaintenanceGate({auth, check, children, reload = () => window.location.reload()}) {
    const [status, setStatus] = useState(() => getReportedMaintenance());
    const [handoffPending, setHandoffPending] = useState(() => hasOperatorHandoff(window.location.hash));
    const [user, setUser] = useState(undefined);
    const [operator, setOperator] = useState(null);
    const [checking, setChecking] = useState(false);
    const blockedRef = useRef(false);

    const maintenance = status?.maintenance === true;
    // A signed-in user whose claims haven't loaded yet renders the app: the api
    // decides either way, and this avoids flashing the page at an operator.
    const exempt = handoffPending || operator === true || (Boolean(user) && operator === null);
    const blocked = maintenance && !exempt;
    blockedRef.current = blocked;

    useEffect(() => subscribeMaintenance(setStatus), []);

    useEffect(() => onAuthStateChanged(auth, next => {
        setUser(next || null);
        setOperator(null);
        if (!next) return;
        setHandoffPending(false);
        next.getIdTokenResult()
            .then(result => { if (auth.currentUser === next) setOperator(result.claims?.operator === true); })
            .catch(() => { if (auth.currentUser === next) setOperator(false); });
    }), [auth]);

    const runCheck = useRef();
    runCheck.current = async () => {
        setChecking(true);
        try {
            const result = await check();
            if (result?.maintenance === true) {
                setStatus({maintenance: true, message: result.message || null});
            } else if (result?.maintenance === false) {
                // Reload rather than remount: the tab may be holding a bundle
                // from before the deploy that the maintenance window was for.
                clearReportedMaintenance();
                if (blockedRef.current) reload();
                else setStatus(null);
            }
        } catch {
            // Offline, rate limited or the api is restarting: keep the current
            // state and try again on the next tick.
        } finally {
            setChecking(false);
        }
    };

    // One check on load so a closed project shows the page before sign-in.
    useEffect(() => { runCheck.current(); }, []);

    useEffect(() => {
        if (!maintenance) return undefined;
        let timer;
        const schedule = () => {
            timer = setTimeout(async () => {
                await runCheck.current();
                schedule();
            }, MAINTENANCE_POLL_MS + Math.random() * POLL_JITTER_MS);
        };
        const wake = () => { if (document.visibilityState !== 'hidden') runCheck.current(); };
        schedule();
        window.addEventListener('focus', wake);
        window.addEventListener('online', wake);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('focus', wake);
            window.removeEventListener('online', wake);
        };
    }, [maintenance]);

    if (blocked) {
        return <MaintenanceScreen message={status.message} checking={checking} onCheck={() => runCheck.current()} />;
    }
    return (
        <>
            {children}
            {maintenance && operator === true && (
                <Theme>
                    <Alert
                        severity="warning"
                        variant="filled"
                        sx={{position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: theme => theme.zIndex.snackbar}}
                    >
                        Maintenance mode is on. Only operator sessions can use this site.
                    </Alert>
                </Theme>
            )}
        </>
    );
}
